// Microbe RTS — unit factory, genome-derived stats, movement, separation, production queue.
//
// Genome effects are read *data-driven* from config.MUTATIONS via colony.genome (id -> level).
// Nothing here hardcodes a balance number; multipliers/costs all come from config.

import type { GameState, Colony, Side, UnitKind, Unit, Vec, Resources } from '../types.js';
import { MUTATIONS, ECON, POP, WORLD, BROOD_COST, CAPTURE, HEALER_UNLOCK_TIER, HEAL, WILD, QUEEN } from '../config.js';
import type { UnitSpec, EvoTarget, Mutation } from '../config.js';
import {
  makeUnit, unitSpec, clampNum, nodeById, ownedCapturable, dist, siegeReach,
} from './world.js';
import { effectiveTarget, attackRange } from './combat.js';

// ---------------------------------------------------------------------------
// Genome-derived getters (pure reads of colony.genome).
// ---------------------------------------------------------------------------
const BASE_UNLOCKED: ReadonlySet<UnitKind> = new Set<UnitKind>([
  'lancer', 'phalanx', 'stinger', 'drone',
]);

function genomeLevel(colony: Colony, id: string): number {
  return colony.genome[id] ?? 0;
}

// A boost targets a unit when its class field names that kind OR is the 'all' sentinel (every unit
// except the Queen — the Queen is excluded at the read sites, never here).
function targets(effClass: EvoTarget | undefined, kind: UnitKind): boolean {
  return !!effClass && (effClass === kind || effClass === 'all');
}
export function classHpMult(colony: Colony, kind: UnitKind): number {
  let m = 1;
  for (const mut of MUTATIONS) {
    const lvl = genomeLevel(colony, mut.id);
    const eff = mut.effect?.hpMult;
    if (lvl > 0 && eff && targets(eff.class, kind)) m *= Math.pow(eff.mult, lvl);
  }
  return m;
}
export function classSpeedMult(colony: Colony, kind: UnitKind): number {
  let m = 1;
  for (const mut of MUTATIONS) {
    const lvl = genomeLevel(colony, mut.id);
    const eff = mut.effect?.speedMult;
    if (lvl > 0 && eff && targets(eff.class, kind)) m *= Math.pow(eff.mult, lvl);
  }
  return m;
}
export function classDmgMult(colony: Colony, kind: UnitKind): number {
  let m = 1;
  for (const mut of MUTATIONS) {
    const lvl = genomeLevel(colony, mut.id);
    const eff = mut.effect?.dmgMult;
    if (lvl > 0 && eff && targets(eff.class, kind)) m *= Math.pow(eff.mult, lvl);
  }
  return m;
}
// ---- Economy multipliers (compound per owned level) ----
function econMult(colony: Colony, pick: (e: NonNullable<Mutation['effect']>) => number | undefined): number {
  let m = 1;
  for (const mut of MUTATIONS) {
    const lvl = genomeLevel(colony, mut.id);
    if (lvl <= 0 || !mut.effect) continue;
    const v = pick(mut.effect);
    if (v != null) m *= Math.pow(v, lvl);
  }
  return m;
}
export const energyMultOf = (c: Colony): number => econMult(c, (e) => e.energyMult);
export const aminoMultOf = (c: Colony): number => econMult(c, (e) => e.aminoMult);
export const buildTimeMultOf = (c: Colony): number => econMult(c, (e) => e.buildTimeMult);
export const buildCostMultOf = (c: Colony): number => econMult(c, (e) => e.buildCostMult);
/** Highest photosynthesis level owned (0 = no photosynthesis). */
export function photoLevelOf(colony: Colony): number {
  let lvl = 0;
  for (const mut of MUTATIONS) {
    if (genomeLevel(colony, mut.id) > 0 && mut.effect?.photoLevel) {
      lvl = Math.max(lvl, mut.effect.photoLevel);
    }
  }
  return lvl;
}
/** Photosynthesis income multiplier (0 when the colony can't photosynthesize). */
export function photoMult(colony: Colony): number {
  const lvl = photoLevelOf(colony);
  if (lvl <= 0) return 0;
  return ECON.photoMultBase + ECON.photoMultPerLevel * (lvl - 1);
}
export function hasToxinResist(colony: Colony): boolean {
  for (const mut of MUTATIONS) {
    if (genomeLevel(colony, mut.id) > 0 && mut.effect?.toxinResist) return true;
  }
  return false;
}
export function isAerobic(colony: Colony): boolean {
  for (const mut of MUTATIONS) {
    if (genomeLevel(colony, mut.id) > 0 && mut.effect?.aerobic) return true;
  }
  return false;
}
export function isUnlocked(colony: Colony, kind: UnitKind): boolean {
  if (BASE_UNLOCKED.has(kind)) return true;
  // Healer is gated purely on reaching Tier III (no mutation to buy — it just unlocks).
  if (kind === 'healer') return colony.tier >= HEALER_UNLOCK_TIER;
  for (const mut of MUTATIONS) {
    if (genomeLevel(colony, mut.id) > 0 && mut.effect?.unlockClass === kind) return true;
  }
  return false;
}
function popPerNodeBonus(colony: Colony): number {
  let b = 0;
  for (const mut of MUTATIONS) {
    const lvl = genomeLevel(colony, mut.id);
    if (lvl > 0 && mut.effect?.popPerNodeBonus) b += mut.effect.popPerNodeBonus * lvl;
  }
  return b;
}
function popCeilingBonus(colony: Colony): number {
  let b = 0;
  for (const mut of MUTATIONS) {
    const lvl = genomeLevel(colony, mut.id);
    if (lvl > 0 && mut.effect?.popCeilingBonus) b += mut.effect.popCeilingBonus * lvl;
  }
  return b;
}
export function popCapFor(state: GameState, side: Side): number {
  const colony = state.colonies[side];
  const forward = ownedCapturable(state, side).length;
  const cap = POP.base + forward * (POP.perForwardNode + popPerNodeBonus(colony));
  return Math.min(POP.ceiling + popCeilingBonus(colony), Math.floor(cap));
}

// ---------------------------------------------------------------------------
// Cost helpers.
// ---------------------------------------------------------------------------
export function canAfford(res: Resources, cost: { energy: number; amino: number; toxin: number }): boolean {
  return res.energy >= cost.energy && res.amino >= cost.amino && res.toxin >= cost.toxin;
}
export function pay(res: Resources, cost: { energy: number; amino: number; toxin: number }): void {
  res.energy -= cost.energy;
  res.amino -= cost.amino;
  res.toxin -= cost.toxin;
}

// ---------------------------------------------------------------------------
// Spawning (applies genome hp multiplier).
// ---------------------------------------------------------------------------
export function spawnUnit(
  state: GameState,
  side: Side,
  kind: UnitKind,
  pos: Vec,
  buildIndex = 0,
): Unit {
  const u = makeUnit(state, side, kind, pos, buildIndex);
  const m = classHpMult(state.colonies[side], kind);
  u.maxHp = u.maxHp * m;
  u.hp = u.maxHp;
  return u;
}

/** Retroactive HP: an hp mutation just bought applies to units ALREADY on the field. Called from
 *  buyMutation with the single level's factor. Scales hp proportionally so a damaged unit keeps its
 *  health %. The Queen is excluded — she carries her own QUEEN.hpMult and stays separate from genome. */
export function applyHpMutationRetro(state: GameState, side: Side, eff: { class: EvoTarget; mult: number }): void {
  for (const u of state.units) {
    if (u.side !== side || u.hp <= 0 || u.queen) continue;
    if (!targets(eff.class, u.kind)) continue;
    u.maxHp *= eff.mult;
    u.hp *= eff.mult;
  }
}

// ---------------------------------------------------------------------------
// Production queue (internal to the sim — not part of the shared GameState).
// Resources + pop are committed at enqueue time; the unit appears after buildSeconds.
// ---------------------------------------------------------------------------
export interface UnitJob { side: Side; kind: UnitKind; nodeId: string; timeLeft: number; buildIndex: number; }
export interface BroodJob { side: Side; nodeId: string; timeLeft: number; }
export interface ProdQueue { units: UnitJob[]; broods: BroodJob[]; buildCounter: number; }

export function newProdQueue(): ProdQueue {
  return { units: [], broods: [], buildCounter: 0 };
}

/** Pending pop weight already committed to the queue for a side. */
export function pendingPop(q: ProdQueue, side: Side): number {
  let p = 0;
  for (const j of q.units) if (j.side === side) p += unitSpec(j.kind).pop;
  return p;
}

/** Enqueue a unit for production. Returns false if gated/unaffordable/pop-capped. */
export function queueUnit(
  state: GameState,
  q: ProdQueue,
  side: Side,
  nodeId: string,
  kind: UnitKind,
): boolean {
  const node = nodeById(state, nodeId);
  const col = state.colonies[side];
  if (!node || node.owner !== side || !node.hasBrood) return false;
  if (!isUnlocked(col, kind)) return false;
  const spec = unitSpec(kind);
  // Genome economy: cheaper units + faster production (multipliers default to 1 when un-teched).
  const costMult = buildCostMultOf(col);
  const cost = {
    energy: spec.cost.energy * costMult,
    amino: spec.cost.amino * costMult,
    toxin: spec.cost.toxin, // toxin cost left unscaled — it's a lineage resource, not a build tax
  };
  if (!canAfford(col.res, cost)) return false;
  if (col.popUsed + pendingPop(q, side) + spec.pop > col.popCap) return false;
  pay(col.res, cost);
  q.units.push({ side, kind, nodeId, timeLeft: spec.buildSeconds * buildTimeMultOf(col), buildIndex: q.buildCounter++ });
  return true;
}

/** Enqueue a forward Brood. Returns false if gated/unaffordable. */
export function queueBrood(state: GameState, q: ProdQueue, side: Side, nodeId: string): boolean {
  const node = nodeById(state, nodeId);
  const col = state.colonies[side];
  if (!node || node.owner !== side || node.type === 'home' || node.hasBrood) return false;
  if (q.broods.some((j) => j.nodeId === nodeId)) return false; // already growing one here — no double-charge
  // BROOD_COST imported lazily via spec-like constant.
  const cost = BROOD;
  if (!canAfford(col.res, cost)) return false;
  pay(col.res, cost);
  q.broods.push({ side, nodeId, timeLeft: BROOD_SECONDS });
  return true;
}
const BROOD = BROOD_COST;
const BROOD_SECONDS = CAPTURE.broodBuildSeconds;

/** Advance production timers; spawn finished units, finish broods. */
export function processProduction(state: GameState, q: ProdQueue, dt: number): void {
  if (q.units.length) {
    // STAGGERED: per node, only the HEAD job (lowest buildIndex) counts down; the rest wait their
    // turn at full time. buildIndex is a unique monotonic counter, so it names the head exactly.
    const headIdx = new Map<string, number>();
    for (const job of q.units) {
      const cur = headIdx.get(job.nodeId);
      if (cur === undefined || job.buildIndex < cur) headIdx.set(job.nodeId, job.buildIndex);
    }
    const keep: UnitJob[] = [];
    for (const job of q.units) {
      const node = nodeById(state, job.nodeId);
      // Cancel if the producing node was lost.
      if (!node || node.owner !== job.side || !node.hasBrood) continue;
      if (headIdx.get(job.nodeId) !== job.buildIndex) {
        keep.push(job); // not the head at this node — hold until the one ahead finishes
        continue;
      }
      job.timeLeft -= dt;
      if (job.timeLeft <= 0) {
        spawnUnit(state, job.side, job.kind, { ...node.rally }, job.buildIndex);
      } else {
        keep.push(job);
      }
    }
    q.units = keep;
  }
  if (q.broods.length) {
    const keep: BroodJob[] = [];
    for (const job of q.broods) {
      const node = nodeById(state, job.nodeId);
      if (!node || node.owner !== job.side) continue;
      job.timeLeft -= dt;
      if (job.timeLeft <= 0) node.hasBrood = true;
      else keep.push(job);
    }
    q.broods = keep;
  }
}

// ---------------------------------------------------------------------------
// Population / derived stats recompute (run once at the top of each tick).
// ---------------------------------------------------------------------------
export function deriveColonies(state: GameState): void {
  for (const side of ['you', 'bot'] as Side[]) {
    const col = state.colonies[side];
    let used = 0;
    for (const u of state.units) {
      if (u.side === side && u.hp > 0) used += unitSpec(u.kind).pop;
    }
    // popUsed reflects LIVE population; queued-but-unbuilt pop is checked separately in queueUnit.
    col.popUsed = used;
    col.popCap = popCapFor(state, side);
  }
}

// ---------------------------------------------------------------------------
// Movement + separation.
// ---------------------------------------------------------------------------
export function moveUnits(state: GameState, dt: number): void {
  for (const u of state.units) {
    if (u.hp <= 0) continue;
    const col = u.side === 'wild' ? null : state.colonies[u.side];
    const spec: UnitSpec = unitSpec(u.kind);
    const baseSpeed =
      spec.speed * (col && !u.queen ? classSpeedMult(col, u.kind) : 1) * (u.queen ? QUEEN.speedMult : 1);
    // Cohesion cap: while an explicit order is active — a move OR an attack (focus/siege) — don't exceed
    // the slowest party member's speed, so a mixed group stays together on the approach instead of the
    // fast units racing ahead of the tanks. Gated on the order fields so a superseding order or arrival
    // (which clears them) drops the cap at once instead of leaving a fast unit stuck slow forever.
    const hasOrder = u.moveTarget != null || u.focusId != null || u.attackNodeId != null;
    const speed =
      u.moveSpeedCap != null && hasOrder
        ? Math.min(baseSpeed, u.moveSpeedCap)
        : baseSpeed;

    let target: Vec | null = null;
    let stopDist = 2;

    const foe = effectiveTarget(state, u);
    // Siege upkeep: keep a sieging unit welded to the enemy core. Clear the siege once the node is gone,
    // captured, or neutralized; otherwise, once the unit has SETTLED (no active move) and separation has
    // nudged it off-core, re-home it to the node center so positional erosion continues. (The !u.moveTarget
    // guard lets the initial sunflower slot move complete before we ever re-home to the center.)
    if (u.attackNodeId) {
      const an = nodeById(state, u.attackNodeId);
      if (!an || an.owner === u.side || an.owner === 'neutral') {
        // Siege ended (node captured / lost / neutralized): release the pack. Clear the slot moveTarget
        // and cohesion cap too, so the victorious band doesn't stay welded (moveTarget outranks foe now)
        // and instead resumes auto-defense at full speed.
        u.attackNodeId = null;
        u.moveTarget = null;
        u.moveSpeedCap = null;
      } else if (!foe && !u.moveTarget) {
        // Hold at WEAPON RANGE of the core, not on top of it — a stinger shells the node from 200
        // away (outside the poison ooze); a melee unit still closes to the rim. Only re-home when
        // the unit has drifted out of its own range.
        const reach = siegeReach(u, an);
        const d = Math.hypot(an.pos.x - u.pos.x, an.pos.y - u.pos.y);
        if (d > reach * 0.9) {
          const hold = reach * 0.8; // approach to just inside range and stop there
          const t = (d - hold) / d;
          u.moveTarget = { x: u.pos.x + (an.pos.x - u.pos.x) * t, y: u.pos.y + (an.pos.y - u.pos.y) * t };
        }
      }
    }
    // An explicit move/siege destination OUTRANKS foe-chase. Two reasons: (1) a navigation tap must be
    // able to PULL a unit off an attack — a fresh 'move' clears focusId, so once moveTarget wins here the
    // unit peels away from auto-aggro too instead of staying welded to a nearby enemy; (2) a siege march
    // must reach the core past a base guard (a foe would otherwise halt it at attackRange*0.9, ~126px,
    // well outside the ~50px core). It still FIRES while marching in — resolveCombat reads effectiveTarget
    // independently. On arrival moveTarget clears (below) so a settled unit resumes auto-defense.
    if (u.moveTarget) {
      target = u.moveTarget;
      stopDist = 3;
    } else if (foe) {
      target = foe.pos;
      stopDist = attackRange(u, foe) * 0.9;
    } else if (u.kind === 'healer') {
      // Idle support drift: slide toward the nearest hurt friendly so it can mend it, but stop
      // just inside heal range instead of piling on top.
      const hurt = nearestHurtFriendly(state, u);
      if (hurt) {
        target = hurt.pos;
        stopDist = HEAL.range * 0.8;
      }
    } else if (u.side === 'wild' && u.guardPos) {
      // No intruder: leash back toward the territory centre (only once it's wandered off).
      if (dist(u.pos, u.guardPos) > WILD.wanderRadius) target = u.guardPos;
    }

    if (!target) {
      u.vel.x = 0;
      u.vel.y = 0;
      continue;
    }

    const dx = target.x - u.pos.x;
    const dy = target.y - u.pos.y;
    const d = Math.hypot(dx, dy);
    if (d <= stopDist) {
      u.vel.x = 0;
      u.vel.y = 0;
      // Reached an explicit move destination -> release it so a settled unit resumes auto-defense (and a
      // repositioned band can be pulled again). A SIEGE slot (attackNodeId set) is kept welded to the core
      // instead of released, so the unit stays on the core eroding it.
      if (u.moveTarget && !u.attackNodeId) {
        u.moveTarget = null;
        u.moveSpeedCap = null; // party arrived; drop the cohesion cap
      }
      continue;
    }
    const vx = (dx / d) * speed;
    const vy = (dy / d) * speed;
    u.vel.x = vx;
    u.vel.y = vy;
    u.pos.x = clampNum(u.pos.x + vx * dt, 0, WORLD.width);
    u.pos.y = clampNum(u.pos.y + vy * dt, 0, WORLD.height);
  }
}

/** Soft separation so units don't fully overlap. O(n^2), fine for scaffold pop sizes. */
export function separation(state: GameState, _dt: number): void {
  const units = state.units;
  const n = units.length;
  for (let i = 0; i < n; i++) {
    const a = units[i];
    if (a.hp <= 0) continue;
    for (let j = i + 1; j < n; j++) {
      const b = units[j];
      if (b.hp <= 0) continue;
      const min = a.radius + b.radius;
      let dx = b.pos.x - a.pos.x;
      let dy = b.pos.y - a.pos.y;
      let d = Math.hypot(dx, dy);
      if (d >= min) continue;
      if (d < 0.0001) {
        // Perfectly overlapped: deterministic nudge by index parity.
        dx = (i % 2 === 0 ? 1 : -1) * 0.5;
        dy = 0.5;
        d = Math.hypot(dx, dy);
      }
      const push = (min - d) * 0.5;
      const ux = dx / d;
      const uy = dy / d;
      // Push each half apart; scale slightly by dt-independent factor (positions, not velocity).
      a.pos.x = clampNum(a.pos.x - ux * push, 0, WORLD.width);
      a.pos.y = clampNum(a.pos.y - uy * push, 0, WORLD.height);
      b.pos.x = clampNum(b.pos.x + ux * push, 0, WORLD.width);
      b.pos.y = clampNum(b.pos.y + uy * push, 0, WORLD.height);
    }
  }
}

/** Queen self-mend: each living queen regenerates a mild fraction of her max hp per second. */
export function resolveRegen(state: GameState, dt: number): void {
  for (const u of state.units) {
    if (!u.queen || u.hp <= 0 || u.hp >= u.maxHp) continue;
    u.hp = Math.min(u.maxHp, u.hp + u.maxHp * QUEEN.regenPerSec * dt);
  }
}

/** Nearest same-side unit below full hp within the healer's seek radius (for idle support drift). */
function nearestHurtFriendly(state: GameState, healer: Unit): Unit | null {
  let best: Unit | null = null;
  let bestD = HEAL.seekRadius;
  for (const u of state.units) {
    if (u === healer || u.side !== healer.side || u.hp <= 0 || u.hp >= u.maxHp) continue;
    const d = dist(healer.pos, u.pos);
    if (d <= bestD) { bestD = d; best = u; }
  }
  return best;
}

/** Healer pass — every healer restores hp to same-side units within HEAL.range (zero attack). */
export function resolveHealing(state: GameState, dt: number): void {
  for (const h of state.units) {
    if (h.kind !== 'healer' || h.hp <= 0) continue;
    for (const u of state.units) {
      if (u === h || u.side !== h.side || u.hp <= 0 || u.hp >= u.maxHp) continue;
      if (dist(h.pos, u.pos) <= HEAL.range) u.hp = Math.min(u.maxHp, u.hp + HEAL.perSec * dt);
    }
  }
}
